import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useAuth } from './auth';
import { useViewMode } from './view-mode-context';
import { useTheme } from 'next-themes';
import { useUserSettingsQuery, useUpdateUserSettingsMutation } from '@/hooks/use-user-settings';
import { UserFeatures, DEFAULT_FEATURES } from './user-settings-types';

export type { UserFeatures };

interface UserSettingsContextType {
  settings: UserFeatures;
  loading: boolean;
  updateSettings: (newFeatures: Partial<UserFeatures>) => Promise<void>;
}

const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined);

export const UserSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { targetUserId } = useViewMode();
  const { theme, setTheme } = useTheme();
  
  const { data: userSettings, isLoading: loading } = useUserSettingsQuery(targetUserId, user?.id);
  const updateSettingsMutation = useUpdateUserSettingsMutation();

  const settings = useMemo(() => {
    if (!userSettings) return DEFAULT_FEATURES;
    
    const features = (userSettings.features as unknown as UserFeatures) || DEFAULT_FEATURES;
    return { 
      ...DEFAULT_FEATURES, 
      ...features, 
      active_session_id: userSettings.active_session_id 
    };
  }, [userSettings]);

  useEffect(() => {
    if (settings.theme && settings.theme !== theme) {
      setTheme(settings.theme);
    }
  }, [settings.theme, theme, setTheme]);

  const updateSettings = async (newFeatures: Partial<UserFeatures>) => {
    if (!user || user.id !== targetUserId) return;

    const { active_session_id, ...restFeatures } = newFeatures;
    const currentFeatures = (userSettings?.features as unknown as UserFeatures) || DEFAULT_FEATURES;
    const updatedFeatures = { ...currentFeatures, ...restFeatures };
    
    // Remove active_session_id from updatedFeatures if it exists there
    delete (updatedFeatures as any).active_session_id;

    await updateSettingsMutation.mutateAsync({
      userId: user.id,
      features: updatedFeatures,
      active_session_id: active_session_id !== undefined ? active_session_id : undefined
    });
  };

  return (
    <UserSettingsContext.Provider value={{ settings, loading, updateSettings }}>
      {children}
    </UserSettingsContext.Provider>
  );
};

export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (context === undefined) {
    throw new Error('useUserSettings must be used within a UserSettingsProvider');
  }
  return context;
};
